<template>
	<InvoicePreview
		show-preview
		:invoice-html="previewHtml || READING_HTML"
		:render-html-as-pdf="Boolean(previewHtml)"
		:pdf-filename="pdfFilename"
		@close-preview="close"
		@download-pdf="downloadPdf"
	>
		<template #sidebar>
			<div class="proposal-import">
				<header class="proposal-import__header">
					<h3>Proposal from Word</h3>
					<p class="proposal-import__file">{{ file.name }}</p>
				</header>

				<p v-if="status === 'reading'" class="proposal-import__status" role="status">
					<span class="proposal-import__spinner" aria-hidden="true"></span>
					Reading the proposal. This can take a minute.
				</p>

				<div v-else-if="status === 'error'" class="proposal-import__status">
					<p class="proposal-import__error" role="alert">{{ error }}</p>
					<button type="button" class="proposal-import__button" @click="read">Try again</button>
				</div>

				<template v-else>
					<dl class="proposal-import__facts">
						<div>
							<dt>Reference</dt>
							<dd>{{ content.reference || '—' }}</dd>
						</div>
						<div>
							<dt>Date</dt>
							<dd>{{ formatProposalDate(content.proposalDate) || '—' }}</dd>
						</div>
						<div>
							<dt>Client</dt>
							<dd>{{ content.clientName || '—' }}</dd>
						</div>
						<div>
							<dt>Properties</dt>
							<dd>{{ propertyNames || '—' }}</dd>
						</div>
					</dl>

					<section v-if="notes.length" class="proposal-import__notes">
						<h4>Please check</h4>
						<ul>
							<li v-for="(note, index) in notes" :key="index">{{ note }}</li>
						</ul>
					</section>

					<p class="proposal-import__hint">
						The Word file is kept with the proposal, and anything can be changed in the editor later.
					</p>
					<p v-if="saveError" class="proposal-import__error" role="alert">{{ saveError }}</p>
					<div class="proposal-import__actions">
						<button
							type="button"
							class="proposal-import__button proposal-import__button--primary"
							:disabled="status === 'saving'"
							@click="save(false)"
						>
							{{ status === 'saving' ? 'Saving…' : 'Save to documents' }}
						</button>
						<button type="button" class="proposal-import__button" :disabled="status === 'saving'" @click="save(true)">
							Save and open
						</button>
					</div>
				</template>
			</div>
		</template>
	</InvoicePreview>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import { useCatalogStore } from '@/stores/catalog';
import { useClientDocumentsStore } from '@/stores/clientDocuments';
import { createAttachmentFromFile } from '@/utils/attachments';
import { deriveClientDocumentFilename, generateClientDocumentHtml } from '@/utils/clientDocumentRegistry';
import { PROPOSAL_DOCUMENT_TYPE, downloadProposalPdfFromHtml, formatProposalDate } from '@/utils/proposalDocument';
import { buildImportedProposalContent, proposalImportNotes } from '@/utils/proposalImport';

// A proposal written in Word, read on the server, shown as it will look before it is kept in
// Documents together with the Word file.
const props = defineProps({
	// The .docx the office chose.
	file: { type: Object, required: true },
	logoUrl: { type: String, default: '' },
	// The footer new proposals carry.
	footerText: { type: String, default: '' },
});

const emit = defineEmits(['saved', 'close']);

const READING_HTML = '<p style="margin:48px 0;color:#6b7280;text-align:center">Reading the Word file…</p>';

const clientDocumentsStore = useClientDocumentsStore();
const catalogStore = useCatalogStore();
const { proxy } = getCurrentInstance() || {};
const logError = proxy?.$logError;

const status = ref('reading');
const error = ref('');
const saveError = ref('');
const content = ref(null);
const notes = ref([]);

const readFailure = (failure) => {
	const code = failure?.response?.status;
	if ([403, 404, 405].includes(code)) return 'Reading Word files arrives with the next server update.';
	if (failure?.code === 'ECONNABORTED') return 'Reading took too long. Try again in a moment.';
	return failure?.response?.data?.error?.message || 'The proposal could not be read. Try again in a moment.';
};

const read = async () => {
	status.value = 'reading';
	error.value = '';
	try {
		const [result] = await Promise.all([
			clientDocumentsStore.readProposalFile(props.file),
			// Client and building names are matched to the catalogs when they are there.
			Promise.resolve(catalogStore.bootstrap?.()).catch(() => null),
		]);
		const fields = result?.fields || {};
		content.value = buildImportedProposalContent({
			fields,
			footerText: props.footerText,
			clientOptions: catalogStore.clientOptions || [],
			buildingOptions: catalogStore.buildingOptions || [],
		});
		notes.value = proposalImportNotes({ fields, content: content.value, warnings: result?.warnings || [] });
		status.value = 'ready';
	} catch (failure) {
		logError?.(failure, 'ProposalImportPreview read');
		error.value = readFailure(failure);
		status.value = 'error';
	}
};

const previewHtml = computed(() =>
	content.value
		? generateClientDocumentHtml({ content: content.value, logoUrl: props.logoUrl || undefined })
		: '',
);

const pdfFilename = computed(() =>
	content.value
		? deriveClientDocumentFilename({ documentType: PROPOSAL_DOCUMENT_TYPE, content: content.value })
		: 'proposal.pdf',
);

const propertyNames = computed(() =>
	(content.value?.properties || []).map((property) => property.name).filter(Boolean).join(', '),
);

const save = async (open) => {
	if (!content.value || status.value === 'saving') return;
	status.value = 'saving';
	saveError.value = '';
	try {
		const saved = await clientDocumentsStore.saveDocument({
			documentId: null,
			payload: {
				documentType: PROPOSAL_DOCUMENT_TYPE,
				content: content.value,
				metadata: { importedFrom: props.file.name },
				supportingDocuments: [createAttachmentFromFile(props.file)],
			},
		});
		emit('saved', { document: saved, open });
	} catch (failure) {
		logError?.(failure, 'ProposalImportPreview save');
		saveError.value = 'The proposal could not be saved. Try again.';
		status.value = 'ready';
	}
};

const downloadPdf = async () => {
	if (!previewHtml.value) return;
	try {
		await downloadProposalPdfFromHtml({ html: previewHtml.value, filename: pdfFilename.value, logError });
	} catch (failure) {
		logError?.(failure, 'ProposalImportPreview downloadPdf');
	}
};

const close = () => {
	if (status.value !== 'saving') emit('close');
};

onMounted(read);
</script>

<style scoped>
.proposal-import {
	display: flex;
	flex-direction: column;
	gap: 16px;
	color: var(--text-color, #333);
	font-size: 0.87rem;
	line-height: 1.45;
}

.proposal-import__header h3 {
	margin: 0;
	font-size: 1.05rem;
	font-weight: 650;
}

.proposal-import__file {
	margin: 4px 0 0;
	color: var(--text-color-secondary, #6b7280);
	overflow-wrap: anywhere;
}

.proposal-import__status {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 10px;
	margin: 0;
	color: var(--text-color-secondary, #6b7280);
}

p.proposal-import__status {
	flex-direction: row;
	align-items: center;
}

.proposal-import__spinner {
	width: 16px;
	height: 16px;
	flex: 0 0 auto;
	border: 2px solid var(--border-color, #e5e7eb);
	border-top-color: var(--noc-blue, #1261c9);
	border-radius: 50%;
	animation: proposal-import-spin 0.8s linear infinite;
}

.proposal-import__error {
	margin: 0;
	color: #b42318;
}

.proposal-import__facts {
	display: grid;
	gap: 8px;
	margin: 0;
}

.proposal-import__facts > div {
	display: grid;
	grid-template-columns: 88px minmax(0, 1fr);
	gap: 8px;
}

.proposal-import__facts dt {
	color: var(--text-color-secondary, #6b7280);
}

.proposal-import__facts dd {
	margin: 0;
	overflow-wrap: anywhere;
}

.proposal-import__notes {
	padding: 12px 14px;
	border-radius: 8px;
	background: var(--report-status-warning-background, #fff7ed);
}

.proposal-import__notes h4 {
	margin: 0 0 6px;
	font-size: 0.87rem;
	font-weight: 650;
}

.proposal-import__notes ul {
	margin: 0;
	padding-left: 18px;
}

.proposal-import__notes li + li {
	margin-top: 4px;
}

.proposal-import__hint {
	margin: 0;
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.82rem;
}

.proposal-import__actions {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.proposal-import__button {
	min-height: 36px;
	padding: 7px 14px;
	border: 1px solid var(--border-color, #d1d5db);
	border-radius: 7px;
	background: var(--noc-background, #fff);
	color: var(--text-color, #333);
	font: inherit;
	font-size: 0.84rem;
	font-weight: 600;
	cursor: pointer;
	touch-action: manipulation;
}

.proposal-import__button--primary {
	border-color: var(--primary-color, #1261c9);
	background: var(--primary-color, #1261c9);
	color: #fff;
}

.proposal-import__button:disabled {
	opacity: 0.6;
	cursor: wait;
}

@media (hover: hover) {
	.proposal-import__button:not(.proposal-import__button--primary):hover:not(:disabled) {
		background: var(--noc-background-secondary, #f5f6f8);
	}

	.proposal-import__button--primary:hover:not(:disabled) {
		background: var(--primary-color-hover, #0d4fa7);
	}
}

@keyframes proposal-import-spin {
	to {
		transform: rotate(360deg);
	}
}

@media (prefers-reduced-motion: reduce) {
	.proposal-import__spinner {
		animation: none;
	}
}
</style>
